<template>
    <div ref="selectionComponent" class="oddsBox show-option">
        <div
            v-bind:class="[
                'odds',
                {
                    odds_rise: isPriceRise && !market.isSuspended,
                    odds_drop: isPriceDrop && !market.isSuspended,
                    odds_selected: isSelected,
                    odds_locked: selection.isSuspended || market.isSuspended,
                    'pointer-events-none': false,
                }]"
            v-on:click="onSelectionClick"
        >
            <div v-if="selection.isSuspended || market.isSuspended" class="odds_locker">
                <SvgIcon iconName="systemIcon_Locked" />
            </div>
            <template v-else>
                <div class="odds-label">
                    {{ selection.name }}
                </div>
                <div class="odds-wrapper">
                    <div class="odds-text">
                        <div class="odds-price" v-bind:class="{ _negative: isPriceNegative }">
                            {{ displayPrice }}
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, ref, toRefs } from 'vue';
    import type { IMarket, IMatch, ISelection } from '@/interface/IMatch';
    import { DisplaySection } from '@/interface/enum';
    import {
        createDynamicMarketTicket,
        createDynamicMarketTicketKey,
    } from '@/libraries/betSlip/ticketHelper';
    import { useBetPage } from '@/libraries/composables/useBetPage';
    import { usePriceChange } from '@/libraries/composables/usePriceChange';
    import { formatMinDecimalPlace } from '@/libraries/utils/text';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useDynamicMarketBetSlipStore } from '@/stores/betSlip/dynamicMarketBetSlipStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'Selection',
        components: {
            SvgIcon,
        },
        props: {
            selection: {
                type: Object as PropType<ISelection>,
                required: true,
            },
            match: {
                type: Object as PropType<IMatch>,
                required: true,
            },
            market: {
                type: Object as PropType<IMarket>,
                required: true,
            },
            displaySection: {
                type: Number as PropType<DisplaySection>,
                default: DisplaySection.Unknown,
            },
        },
        setup(props) {
            const { addSelection, removeTicket } = useDynamicMarketBetSlipStore();
            const { betSlip } = storeToRefs(useDynamicMarketBetSlipStore());
            const {
                isTicketExist: isSingleModeTicketExist,
                addOrRemoveTicket: addOrRemoveSingleModeTicket,
            } = useSingleModeBetSlipStore();
            const { isSingleModeBetSlipEnabled } = storeToRefs(useFeatureToggleStore());
            const { showErrorToast, clearTopScreenToast, setOddsPosition } = useRootStore();
            const { isAsi, marketPage } = storeToRefs(useRootStore());
            const { match, selection, market, displaySection } = toRefs(props);

            const displayPrice = computed(() => formatMinDecimalPlace(selection.value.price.value, 2));
            const isPriceNegative = computed(() => selection.value.price.value < 0);
            const { isShowBetSlip, isShowDynamicBetSlip } = storeToRefs(useBetSlipStore());
            const singleModeTicketKey = computed(() => createDynamicMarketTicketKey(selection.value.id));
            const isSelected = computed(() => (
                isSingleModeBetSlipEnabled.value
                    ? isSingleModeTicketExist(singleModeTicketKey.value)
                    : isShowDynamicBetSlip.value && betSlip.value?.selection.id === selection.value.id
            ));

            const price = computed(() => selection.value.price);
            const { isPriceRise, isPriceDrop } = usePriceChange(price);

            const { betPage } = useBetPage(marketPage.value, displaySection.value);

            const selectionComponent = ref();
            async function onSelectionClick() {
                if (!isAsi.value) {
                    window.eventEmitter.emit('pp.showLogin', 'Sports');
                    return;
                }

                clearTopScreenToast();
                if (selection.value.isSuspended || market.value.isSuspended) {
                    showErrorToast('betSlip_errorMessage_oddsClosed');
                    return;
                }

                if (isSingleModeBetSlipEnabled.value) {
                    const ticket = createDynamicMarketTicket(
                        match.value,
                        market.value,
                        selection.value,
                        betPage.value,
                    );
                    await addOrRemoveSingleModeTicket(ticket);
                    return;
                }

                if (isSelected.value) {
                    await removeTicket();
                    return;
                }

                await addSelection(match.value, props.market, props.selection, betPage.value);

                if (selectionComponent.value) {
                    if (isShowBetSlip.value) {
                        setOddsPosition(selectionComponent.value.getBoundingClientRect().y);
                    } else {
                        setTimeout(() => {
                            setOddsPosition(selectionComponent.value.getBoundingClientRect().y);
                        }, 300);
                    }
                }
            }

            return {
                displayPrice,
                isPriceNegative,
                isSelected,
                isPriceRise,
                isPriceDrop,
                onSelectionClick,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/selection";
</style>
